import { effectApplicationKey } from './effect-application-key.js';
/**
 * Mutable simulator state. Mirrors the report's required state:
 * position/velocity, HP/shield, resources, cooldowns/charges, statuses/stacks,
 * action queue, target/aggro, cast state, objective zone, time.
 */

import type { Archetype, MonsterBehavior } from './archetypes.js';
import type { Skill } from '../types.js';
import type { Terrain, Vec2 } from '../terrain.js';
import type { CombatDefenses, CombatShield, DamageComponent } from './combat.js';
import type { BaseStats, EffectiveStats, StatModifier } from './stats.js';
import type { RootContextHandle } from './root-context.js';
import type { ModifierSources, PassiveDefinition, PassiveRuntime } from './passives.js';
import type { ToggleState } from './channel.js';
import type { OwnedSkill } from './skill-runtime.js';
import type { ResolvedTargetGoal } from './targeting.js';
import type { PrimaryStats } from './primary-stats.js';
import type { CombatSourceDefinition, SourceSnapshot } from './source-profile.js';
import type { IntrinsicMagicSourceDefinition } from './magic-source.js';
import type { SkillMasterySnapshot } from './mastery.js';
import type { ElementParentState } from './element-package.js';
export type { Vec2 } from '../terrain.js';

export type MonsterLifecycle = 'IDLE' | 'ENGAGED' | 'RETURN' | 'LEASH';
export interface MonsterChargeState {
  phase: 'WINDUP' | 'DASH' | 'RECOVERY';
  targetPlayerId: number;
  startedTick: number;
  chargeWindupDelayTicks?: number;
  direction?: Vec2;
  /** Target position captured when WINDUP ends; never changes during DASH. */
  targetPoint?: Vec2;
  remainingDistanceM?: number;
  readonly hitPlayerIds: readonly number[];
}
export interface MonsterAiState {
  lifecycle: MonsterLifecycle;
  targetPlayerId: number | null;
  /** Support aggro may not recursively propagate through another supporter. */
  targetSource?: 'PROACTIVE' | 'DAMAGE' | 'SUPPORT';
  charge?: MonsterChargeState;
  wander?: { phase: 'MOVE' | 'PAUSE'; target: Vec2 };
}

export type StatusKind = 'stun' | 'root' | 'silence' | 'disarm' | 'slow' | 'dot' | 'heal_reduction' | 'damage_amp' | 'damage_reduction' | 'mark' | 'neutral';

/** The applier of a status; player and enemy ids are separate namespaces. */
export type OwnerKey = { readonly kind: 'player'; readonly id: number } | { readonly kind: 'enemy'; readonly id: number };

/** A payload-free periodic schedule: it raises a TICKED event and nothing else. */
export interface StatusTimer {
  readonly intervalTicks: number;
  nextTick: number;
}

/** A timed status on an enemy (or the player). */
export interface Status {
  readonly instanceId: number;
  readonly definitionId: string;
  readonly kind: StatusKind;
  /**
   * Who applied it. Statuses built outside `applyStatus` (fixtures, hand-made literals) may lack it; every
   * production application records it.
   */
  readonly ownerKey?: OwnerKey;
  /** The live instance on the same actor this one ends with; set once, at application. */
  boundToInstanceId?: number;
  readonly appliedTick: number;
  expireTick: number | null;
  timer?: StatusTimer;
  actionDelayTicks?: number;
  nextPeriodicTick?: number;
  readonly periodicIntervalTicks?: number;
  /** Always 1 at creation; only a `mark` raises it (`applyLeaf`). */
  stacks: number;
  readonly tags: readonly string[];
  readonly tag?: string;
  /**
   * Offline simulator's private seconds clock, decremented by its `dt` step
   * (like `SimState.t`). The runtime/network authority never branches on it —
   * expiry there is `expireTick` vs the tick clock. Seeded at creation to
   * `(expireTick - appliedTick) / tickRate`.
   */
  remainingS: number;
  // effect-specific payload
  readonly slowFraction?: number;
  readonly healReductionFraction?: number;
  readonly ampFraction?: number;
  readonly drFraction?: number;
  // dot bookkeeping — `tickS`/`sinceTickS` are the offline simulator's seconds
  // mirror of `periodicIntervalTicks`/`nextPeriodicTick`, same ownership as `remainingS`.
  readonly tickS?: number;
  readonly amountPerTick?: number;
  sinceTickS?: number;
  /** Periodic damage keeps its source facts; it never creates a normal HitEvent. */
  readonly damageComponent?: DamageComponent;
  readonly healAmountPerTick?: number;
  readonly sourceEnemyId?: number;
  /** A damage-over-time status carries one of these per authored dot and source: applying the same key again replaces it (`refreshStatus`). */
  readonly refreshKey?: string;
  rootContext?: RootContextHandle;
  /** Replaced as a whole when an element parent takes a newer application's snapshot. */
  causal?: {
    readonly executionId?: number; readonly root?: RootContextHandle; readonly attackId?: number; readonly stageId?: string;
    /** The causal generation of the attack that applied it: what a hook released by the status is one below. */
    readonly generation?: number;
  };
  /** Present on an element parent: which package and stage it is in, whose cast made it last, and what that cast realized. */
  element?: ElementParentState;
}

export interface StatusCreationContext {
  readonly currentTick: number;
  readonly tickRate: number;
  allocateStatusInstanceId(): number;
}

export interface CreateStatusInput {
  readonly kind: StatusKind;
  readonly definitionId?: string;
  readonly ownerKey?: OwnerKey;
  /** A payload-free periodic event schedule; exclusive with the periodic damage/heal fields. */
  readonly actionDelayTicks?: number;
  readonly timerIntervalS?: number;
  readonly timerIntervalTicks?: number;
  readonly durationS?: number | null;
  readonly expireTick?: number | null;
  readonly periodicIntervalS?: number;
  readonly periodicIntervalTicks?: number;
  readonly tags?: readonly string[];
  readonly tag?: string;
  readonly slowFraction?: number;
  readonly healReductionFraction?: number;
  readonly ampFraction?: number;
  readonly drFraction?: number;
  readonly damageComponent?: DamageComponent;
  readonly healAmountPerTick?: number;
  readonly amountPerTick?: number;
  readonly sourceEnemyId?: number;
  readonly refreshKey?: string;
  readonly rootContext?: RootContextHandle;
  readonly causal?: Status['causal'];
  readonly element?: ElementParentState;
}

/**
 * The ticks a finite status of `durationS` seconds lives: at least one, the seconds rounded up. Every reader of a status's
 * lifetime (creation, a refresh, a timer's count, the price of what a status makes possible) quantizes with this.
 */
export function statusDurationTicks(durationS: number, tickRate: number): number {
  return Math.max(1, Math.ceil(durationS * tickRate));
}

/** The ticks between two firings of a status's timer of `intervalS` seconds: the seconds rounded up. */
export function statusIntervalTicks(intervalS: number, tickRate: number): number {
  return Math.ceil(intervalS * tickRate);
}

function safeTick(value: number): boolean { return Number.isSafeInteger(value) && value >= 0; }
function safePositive(value: number): boolean { return Number.isSafeInteger(value) && value > 0; }
/** Canonical status factory. It has no global fallback: callers own identity. */
export function createStatus(input: CreateStatusInput, context: StatusCreationContext): Status | null {
  if (!safeTick(context.currentTick) || !(Number.isFinite(context.tickRate) && context.tickRate > 0)) throw new Error('invalid status creation context');
  const instanceId = context.allocateStatusInstanceId();
  if (!safePositive(instanceId)) throw new Error('status instance IDs must be positive safe integers');
  let expireTick: number | null;
  if (input.expireTick !== undefined) expireTick = input.expireTick;
  else if (input.durationS === null || input.durationS === undefined) expireTick = null;
  else {
    if (!(Number.isFinite(input.durationS) && input.durationS >= 0)) throw new Error('invalid status duration');
    if (input.durationS === 0) return null;
    expireTick = context.currentTick + statusDurationTicks(input.durationS, context.tickRate);
  }
  if (expireTick !== null && !safeTick(expireTick)) throw new Error('invalid status expiry tick');
  const periodic = input.periodicIntervalTicks ?? (input.periodicIntervalS === undefined ? undefined : statusIntervalTicks(input.periodicIntervalS, context.tickRate));
  const damageComponent = input.damageComponent === undefined ? undefined : Object.freeze({ ...input.damageComponent });
  const healAmountPerTick = input.healAmountPerTick ?? (input.amountPerTick !== undefined && input.amountPerTick < 0 ? -input.amountPerTick : undefined);
  const timerTicks = input.timerIntervalTicks ?? (input.timerIntervalS === undefined ? undefined : statusIntervalTicks(input.timerIntervalS, context.tickRate));
  if (timerTicks !== undefined && (periodic !== undefined || !safePositive(timerTicks))) throw new Error('invalid status timer');
  if (periodic === undefined) {
    if (damageComponent !== undefined || healAmountPerTick !== undefined) throw new Error('non-periodic status cannot carry periodic payload');
  } else {
    if (!safePositive(periodic) || (damageComponent === undefined) === (healAmountPerTick === undefined) || (healAmountPerTick !== undefined && !(healAmountPerTick > 0))) throw new Error('invalid periodic status');
  }
  const tags = Object.freeze([...new Set(input.tags ?? (input.tag === undefined ? [] : [input.tag]))].sort((a, b) => a < b ? -1 : a > b ? 1 : 0));
  if (input.causal?.root && !input.rootContext) throw new Error('causal root requires status root ownership');
  if (input.rootContext && input.causal?.root && (input.rootContext.index !== input.causal.root.index || input.rootContext.generation !== input.causal.root.generation)) throw new Error('periodic causal root must equal status root');
  const durationS = expireTick === null ? Infinity : Math.max(0, expireTick - context.currentTick) / context.tickRate;
  return { instanceId, definitionId: input.definitionId ?? `builtin:${input.kind}`, kind: input.kind,
    ...(input.ownerKey === undefined ? {} : { ownerKey: Object.freeze({ ...input.ownerKey }) }),
    appliedTick: context.currentTick, expireTick, ...(periodic === undefined ? {} : { periodicIntervalTicks: periodic, nextPeriodicTick: context.currentTick + periodic }),
    ...(timerTicks === undefined ? {} : { timer: { intervalTicks: timerTicks, nextTick: context.currentTick + timerTicks } }),
    actionDelayTicks: input.actionDelayTicks,
    stacks: 1, tags, tag: input.tag, remainingS: durationS, tickS: (periodic ?? timerTicks) === undefined ? undefined : (periodic ?? timerTicks)! / context.tickRate,
    sinceTickS: (periodic ?? timerTicks) === undefined ? undefined : 0, slowFraction: input.slowFraction, healReductionFraction: input.healReductionFraction,
    ampFraction: input.ampFraction, drFraction: input.drFraction, damageComponent, healAmountPerTick,
    amountPerTick: input.amountPerTick !== undefined && input.amountPerTick >= 0 ? input.amountPerTick : undefined,
    sourceEnemyId: input.sourceEnemyId, refreshKey: input.refreshKey, rootContext: input.rootContext,
    causal: input.causal ? Object.freeze({ ...input.causal }) : input.rootContext ? Object.freeze({ root: input.rootContext }) : undefined,
    ...(input.element === undefined ? {} : { element: input.element }) };
}

export interface Enemy {
  id: number;
  /** Immutable spawn-time level; simulation never infers it from position. */
  level: number;
  archetype: Archetype;
  pos: Vec2;
  hp: number;
  maxHp: number;
  alive: boolean;
  statuses: Status[];
  /** damage dealt to the player so far (for aggro/threat accounting). */
  dealt: number;
  shields: CombatShield[];
  combatDefenses?: CombatDefenses;
  attackSpec: import('./archetypes.js').MonsterAttackSpec;
  nextAttackTick: number;
  shockDelays?: { tick: number; ticks: number }[];
  /** Separate charge recast clock; absent on legacy/non-charge actors. */
  nextChargeTick?: number;
  targetPlayerId: number | null;
  velocity: Vec2;
  /** Present only for explicitly authored behavior; omitted is legacy AI. */
  behavior?: MonsterBehavior;
  homePosition?: Vec2;
  aiState?: MonsterAiState;
  /** Authoritative incoming damage memory used by REACTIVE monsters. */
  damageByPlayer?: Map<number, number>;
  lastDamagedTickByPlayer?: Map<number, number>;
}

/**
 * Runtime clamp on how much a target's movement may be slowed by active
 * statuses, however many overlap. Deliberately distinct from
 * `BalanceProfile.limits.slowCap`, which is an *authoring/valuation* cap on a
 * single `slow` leaf (safety.ts rejects a leaf at or above it and tells the
 * author to use `root` instead). The two happen to share a value today; they
 * answer different questions and must stay separately adjustable.
 */
export const MAX_RUNTIME_SLOW_FRACTION = 0.9;

/**
 * The single reader for "how slowed is this actor right now": the strongest
 * active positive `slowFraction`, clamped. Move-speed *buffs* are stored as
 * negative `slowFraction` and are never a slow — `Math.max` against 0 drops
 * them here, and `resolvePlayerMoveSpeedFactor` owns reading that other side.
 */
export function strongestSlowFraction(statuses: readonly Status[]): number {
  let slow = 0;
  for (const status of statuses) {
    if (effectApplicationKey(status) === effectApplicationKey({ kind: 'slow' })) slow = Math.max(slow, status.slowFraction ?? 0);
  }
  return Math.min(MAX_RUNTIME_SLOW_FRACTION, Math.max(0, slow));
}

/** @deprecated Use CombatShield. */
export type Shield = CombatShield;

/**
 * One loadout entry. `owned` is the single owner of every per-entry runtime
 * fact — definition, canonical source, cooldown, charges. The mirrored
 * `skill`/`canonicalSkill`/`cdRemainingS`/`chargesRemaining`/`chargeTimerS`/
 * `definition` fields that used to sit alongside it were dual-written copies
 * of exactly those values; they are gone, so no reader can observe a stale
 * half. Reach the facts through the accessors below rather than `owned`
 * directly, so a later ownership change has one place to land.
 */
export interface LoadoutSkill {
  owned: OwnedSkill;
  /** true if this is the skill under evaluation (vs a control filler). */
  underTest?: boolean;
  ownerId?: string;
  toggle?: ToggleState;
}

export function loadoutDefinition(loadout: LoadoutSkill): Readonly<Skill> { return loadout.owned.definition; }
export function cooldownRemaining(loadout: LoadoutSkill): number { return loadout.owned.cooldownRemainingS; }
export function setCooldownRemaining(loadout: LoadoutSkill, value: number): void { loadout.owned.cooldownRemainingS = value; }
export function chargesRemaining(loadout: LoadoutSkill): number | undefined { return loadout.owned.chargesRemaining; }
export function setChargesRemaining(loadout: LoadoutSkill, value: number | undefined): void { loadout.owned.chargesRemaining = value; }
export function chargeCarry(loadout: LoadoutSkill): number | undefined { return loadout.owned.chargeCarryS; }
export function setChargeCarry(loadout: LoadoutSkill, value: number | undefined): void { loadout.owned.chargeCarryS = value; }

/** Global cast/action state: at most one skill can own the actor at a time. */
export interface CastState {
  skillId: string;
  loadoutIndex: number;
  phase: 'startup' | 'recovery';
  /** Time until release (startup) or until another action may start (recovery). */
  remainingS: number;
  /** Simulation timestamp at which the cast was committed. */
  startedAtS: number;
  /**
   * TARGET.1: the goal acquired and locked at cast start for a
   * `skill.targeting`-bearing skill (contract §8.1). Immutable for the life
   * of this `CastState`; release consumes it as-is instead of re-acquiring.
   * Absent for legacy (`targeting`-less) skills — no behavior change for them.
   * Dropped exactly where `CastState` itself is already dropped (cast
   * cancel/Down transition), so it carries no independent leak surface.
   */
  resolvedGoal?: ResolvedTargetGoal;
  /**
   * ACTIVE.1: the source facts resolved and frozen at accepted activation
   * start (active-skill-mechanics-addendum-v0.1.md §6). Immutable for the
   * life of this `CastState`; release consumes it as-is instead of
   * re-resolving the owner's current equipped source/Primary allocation
   * (STOP §1843-1846 "source reread at release"). Absent for skills with no
   * `sourceRelativeTiming`/`sourceRelative` damage leaves — no behavior
   * change for them. Independent of `resolvedGoal`.
   */
  sourceSnapshot?: SourceSnapshot;
  /**
   * MASTERY.1 Stage B: the Mastery facts (multiplier + realized costs)
   * captured once at accepted activation start, mirroring `sourceSnapshot`
   * exactly — immutable for the life of this `CastState`; release consumes
   * it as-is instead of rereading the owner's current `masteryLevel` (D10).
   */
  masterySnapshot?: SkillMasterySnapshot;
  /**
   * `2026-09-13-player-power-curve-adjustment.md` §2.3/§3.1 — WIL cast-save
   * roll result for the CC application currently holding `isCastLocked`
   * true, resolved exactly once per interrupt event rather than once per
   * tick (a per-tick roll would make any multi-tick stun a near-certain
   * interrupt). `undefined` until the first locked tick rolls it; reset to
   * `undefined` whenever the actor is not cast-locked, so a later, separate
   * CC application during the same cast gets its own fresh roll.
   */
  ccInterruptSaved?: boolean;
}

export interface Player {
  readonly id: number;
  pos: Vec2;
  hp: number;
  maxHp: number;
  /**
   * `2026-09-13-player-power-curve-adjustment.md` §2.1/§2.2 — VIT-driven,
   * absolute (not a multiplier), mirroring `manaRegenPerS`.
   */
  hpRegenPerS: number;
  mana: number;
  maxMana: number;
  manaRegenPerS: number;
  /**
   * WIL-driven CC duration multiplier `(1 - wilResist)`, the player-side
   * mirror of `Archetype.ccSusceptibility` (§2.3/§7 item 2 — dormant until a
   * monster -> player CC delivery path exists to consume it; that path is
   * explicitly out of scope here).
   */
  ccSusceptibility: number;
  energy: number;
  maxEnergy: number;
  energyRegenPerS: number;
  baseSpeed: number;
  alive: boolean;
  down: boolean;
  shields: CombatShield[];
  /** Authored defenses; event-time defense is derived by the caller and never cached. */
  readonly baseCombatDefenses?: CombatDefenses;
  /** timed self-buffs (move speed etc.) as statuses. */
  statuses: Status[];
  /** Startup/recovery lock shared by the whole loadout. */
  cast: CastState | null;
  /** zone membership from the previous tick for enter/exit telemetry. */
  zoneIds: Set<string>;
  /**
   * TARGET.1 gameplay-facing state for `MOVE_DIRECTION` (contract §2.2 melee
   * default, "last valid non-zero movement direction"). Authoritative and
   * deterministic — never presentation-only. `undefined` until the first
   * non-zero movement input has been observed; no default direction is
   * invented before then (audit §F).
   */
  facing?: Vec2;
  /** Source-side combat values; effective stats are rebuilt when modifiers change. */
  baseStats?: BaseStats;
  statModifiers?: StatModifier[];
  effectiveStats?: EffectiveStats;
  statsDirty?: boolean;
  modifierSources?: ModifierSources;
  modifierSourceVersion?: number;
  passiveDefinitions?: readonly PassiveDefinition[];
  passiveRuntime?: PassiveRuntime;
  /**
   * ACTIVE.1 owner-scoped Primary Stat allocation (audit §D — deliberately
   * separate from `baseStats`/`effectiveStats`, which are outgoing
   * combat-modifier structures, not this six-stat allocation model). Absent
   * is treated as `ZERO_PRIMARY_STATS` wherever a source profile is resolved
   * — never a runtime-generated default, a fixed identity element (work
   * instruction §158).
   */
  primaryStats?: PrimaryStats;
  /**
   * ACTIVE.1 minimum owner-scoped combat source seam (audit §G): the
   * resolved definition directly, no inventory/slot/id-lookup layer, and no
   * full equipment product system. Absent means this owner has no
   * source-relative-capable weapon/catalyst equipped.
   */
  equippedSource?: CombatSourceDefinition;
  /**
   * SOURCE.2 owner/runtime seam (contract §21/§25):
   * every character has intrinsic MAGIC even with no Catalyst equipped, so
   * this is the smallest optional field required to carry that owner data —
   * no inventory/progression system, no continuous level formula. Absent
   * intrinsic data combined with a MAGIC cast and no Catalyst provider is a
   * runtime integration error (missing owner data), never a gameplay "no
   * magic permission" rejection — see `resolveCastMagicSourceSnapshot`.
   */
  intrinsicMagicSource?: IntrinsicMagicSourceDefinition;
}

export interface SimState {
  t: number;
  player: Player;
  enemies: Enemy[];
  loadout: LoadoutSkill[];
  nextEnemyId: number;
  /** objective/arena radius; enemies spawn on its edge. */
  arenaRadiusM: number;
  terrain: Terrain;
}
